import React, { useEffect, useRef, useState } from 'react';
import {
  Typography,
  Box,
  Container,
  Card,
  CardHeader,
  Divider,
  CardContent,
  IconButton,
  Tab,
  Tabs, Button, Dialog, DialogTitle, DialogContent, DialogActions, Stack, Chip,
  Hidden
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorIcon from '@mui/icons-material/Error';
import CancelIcon from '@mui/icons-material/Cancel';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import * as mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import moment from 'moment';
import Table from '../components/Table';
import { AxiosCompanyDashboardFleetIoConfigsClient } from '../proxy/ProxyClients';
import FleetIoService from '../services/FleetIoService';
import EnumService from '../services/EnumService';
import { EnumRes, FleetIoForm } from '../proxy/proxy';
import AppConstants from '../constants/AppConstants';
import MobileDataList from '../components/MobileDataList';

const MAPBOX_TOKEN = AppConstants.MAPBOX_KEY;

// The following is required to stop "npm build" from transpiling mapbox code.
// notice the exclamation point in the import.
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const VehicleWashSchedule: React.FC = () => {
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [states, setStates] = useState<EnumRes[]>([]);
  const [selectedStateIdx, setSelectedStateIdx] = useState<number>(0);
  const [forms, setForms] = useState<FleetIoForm[]>([]);
  const [days, setDays] = useState<string>('15');
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortModel, setSortModel] = useState<any>();
  const [selectedSummary, setSelectedSummary] = useState<string>('');
  const [counts, setCounts] = useState({
    breached: 0,
    warning: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const statesRes = await EnumService.getEnums('states');
        const fleetIoFormTypes = await EnumService.getEnums('fleetIoFormTypes');
        const truckWashFleetIoFormTypeId = fleetIoFormTypes.find((ft) => ft.value === 'TruckWash')?.id || 0;
        const configRes = await AxiosCompanyDashboardFleetIoConfigsClient.get();
        const truckWashForms = configRes?.config?.forms?.filter((x) => x.fleetIoFormType === truckWashFleetIoFormTypeId) || [];
        setForms(truckWashForms);
        // Only show states for which at least one form is configured (stateIds includes state.id)
        const filteredStates = (statesRes || []).filter((state: EnumRes) =>
          truckWashForms.some((form: FleetIoForm) => Array.isArray(form.stateIds) && form.stateIds.includes(state.id ?? 0)));
        setStates(filteredStates);
        if (!filteredStates?.length || !truckWashForms?.length) {
          setSubmissions([]);
          setLoading(false);
          return;
        }
        // Only fetch for selected state
        const state = filteredStates[selectedStateIdx];
        const validForms = truckWashForms.filter((form: FleetIoForm) => !!form.id && Array.isArray(form.stateIds) && form.stateIds.includes(state.id ?? 0));
        const submittedAfter = days && !Number.isNaN(Number(days)) && Number(days) > 0
          ? moment().subtract(Number(days), 'days').startOf('day').toISOString()
          : '';
        // Helper to fetch all pages for a form
        const fetchAllPages = async (form: FleetIoForm) => {
          let allRecords: any[] = [];
          let startCursor = '';
          let hasNext = true;
          while (hasNext) {
            const payload = {
              actionId: 905,
              stateId: state.id,
              queryParams: {
                inspection_form_id: form.id?.toString() || '',
                submittedAfter,
                startCursor,
              }
            };
            // eslint-disable-next-line no-await-in-loop
            const response = await FleetIoService.fleetIoPage(payload);
            if (response?.records?.length > 0) {
              allRecords = allRecords.concat(
                response.records.map((record: any) => ({
                  ...record,
                  state: state.id,
                  formType: 'Truck Wash'
                }))
              );
            }
            if (response?.next_cursor) {
              startCursor = response.next_cursor;
            } else {
              hasNext = false;
            }
          }
          return allRecords;
        };
        const recordsArrays = await Promise.all(
          validForms.map((form: FleetIoForm) => fetchAllPages(form))
        );
        const allSubmissions = recordsArrays.flat();

        // Calculate days since submission and categorize
        let breachedCount = 0;
        let warningCount = 0;

        const processedSubmissions = allSubmissions.map((submission: any) => {
          const submittedAt = moment(submission.submitted_at);
          const daysSinceSubmission = moment().diff(submittedAt, 'days');

          let isBreached = false;
          let isWarning = false;

          if (daysSinceSubmission > 14) {
            isBreached = true;
            breachedCount++;
          } else if (daysSinceSubmission > 11) {
            isWarning = true;
            warningCount++;
          }

          return {
            ...submission,
            daysSinceSubmission,
            isBreached,
            isWarning
          };
        });

        setCounts({ breached: breachedCount, warning: warningCount });
        setSubmissions(processedSubmissions);
      } catch (err) {
        setError('No records available in the selected state');
        setSubmissions([]);
        setCounts({ breached: 0, warning: 0 });
      }
      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line
  }, [selectedStateIdx, days]);

  const handleStateTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedStateIdx(newValue);
  };

  const handleDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDays(event.target.value.replace(/[^0-9]/g, ''));
  };

  const handleViewSubmission = async (row: any) => {
    setDialogLoading(true);
    setDialogOpen(true);
    try {
      const payload = {
        actionId: 904,
        stateId: row.state,
        queryParams: {
          id: row.id.toString()
        }
      };
      const response = await FleetIoService.fleetIoPage(payload);
      setSelectedSubmission(response);
    } catch (err) {
      setSelectedSubmission({ error: 'Failed to fetch submission' });
    }
    setDialogLoading(false);
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 120 },
    { field: 'date', headerName: 'Date', width: 120 },
    // { field: 'inspection_form', headerName: 'Form', width: 200, valueGetter: (params: any) => params.row.inspection_form?.title },
    { field: 'user', headerName: 'User', flex: 1, valueGetter: (params: any) => params.row.user?.name },
    { field: 'vehicle', headerName: 'Vehicle', width: 180, valueGetter: (params: any) => params.row.vehicle?.name },
    { field: 'license_plate', headerName: 'License Plate', width: 120, valueGetter: (params: any) => params.row.vehicle?.license_plate },
    { field: 'submitted_at', headerName: 'Submitted At', width: 180, valueGetter: (params: any) => moment(params.row.submitted_at)?.format('LLL') },
    { field: 'failed_items', headerName: 'Failed Items', width: 120 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params: any) => (
        <Button variant="outlined" size="small" onClick={() => handleViewSubmission(params.row)}>
          View
        </Button>
      )
    }
  ];

  useEffect(() => {
    if (!dialogOpen || !mapContainerRef.current) return;
    // Delay map initialization to ensure container is visible
    setTimeout(() => {
      const pins = (selectedSubmission?.submitted_inspection_items || []).filter(
        (item: any) => item.latitude && item.longitude
      );
      if (pins.length === 0) return;
      // Default center: first pin or fallback
      const center = pins.length > 0
        ? [selectedSubmission.submitted_longitude, selectedSubmission.submitted_latitude]
        : [-122.4194, 37.7749];
      // Create map
      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center,
        zoom: 12,
        accessToken: MAPBOX_TOKEN
      });
      mapRef.current = map;
      map.addControl(new mapboxgl.NavigationControl());
      const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false });
      pins.forEach((item: any) => {
        const el = document.createElement('div');
        el.className = 'marker';
        el.style.backgroundImage = 'url(https://docs.mapbox.com/mapbox-gl-js/assets/custom_marker.png)';
        el.style.width = '30px';
        el.style.height = '38px';
        el.style.backgroundSize = '100%';
        el.style.backgroundRepeat = 'no-repeat';
        new mapboxgl.Marker(el)
          .setLngLat([item.longitude, item.latitude])
          .setPopup(
            new mapboxgl.Popup({ offset: 25 })
              .setHTML(`
                <div>
                  <h3>${item.inspection_item?.label || 'Pin'}</h3>
                  <p><strong>Lat:</strong> ${item.latitude}</p>
                  <p><strong>Long:</strong> ${item.longitude}</p>
                </div>
              `)
          )
          .addTo(map);
      });
    }, 1000);
  }, [dialogLoading]);

  const renderSubmissionDialog = () => {
    const pins = (selectedSubmission?.submitted_inspection_items || []).filter(
      (item: any) => item.latitude && item.longitude
    );
    return (
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedSubmission?.inspection_form?.title || 'Submission Details'}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {selectedSubmission?.submitted_at && (
              <Chip label={`Submitted: ${moment(selectedSubmission.submitted_at).format('LLL')}`} color="primary" size="small" />
            )}
            {selectedSubmission?.duration && (
              <Chip label={`Duration: ${selectedSubmission.duration}`} size="small" variant="outlined" />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            if (dialogLoading) {
              return <Typography>Loading...</Typography>;
            }
            if (selectedSubmission?.error) {
              return <Typography color="error">{selectedSubmission.error}</Typography>;
            }
            return (
              <>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {/* <b>Vehicle:</b> {selectedSubmission?.vehicle?.name} ({selectedSubmission?.vehicle?.license_plate})<br /> */}
                  <b>User:</b> {selectedSubmission?.user}<br />
                  <b>Failed Items:</b> {selectedSubmission?.failed_items}<br />
                  <b>Images Count:</b> {selectedSubmission?.images_count}<br />
                  <b>Comments Count:</b> {selectedSubmission?.comments_count}<br />
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <b>Submitted Inspection Items:</b>
                </Typography>
                {/* Mapbox map for pins */}
                {pins.length > 0 && (
                  <Box sx={{ height: 300, mb: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid #eee' }}>
                    <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
                  </Box>
                )}
                {selectedSubmission?.submitted_inspection_items?.map((item: any) => {
                  const type = item.inspection_item?.type;
                  return (
                    <Box key={item.id} sx={{ mb: 2, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                      <Typography variant="body2"><b>{item.inspection_item?.label}</b></Typography>
                      {type === 'InspectionDropdownItem' && (
                        <Typography variant="body2">Result: {item.result?.label || 'N/A'}</Typography>
                      )}
                      {type === 'InspectionFreeTextItem' && (
                        <Typography variant="body2">Text: {item.result?.text || 'N/A'}</Typography>
                      )}
                      {type === 'InspectionPhotoItem' && (
                        <>
                          <Typography variant="body2">Photo:</Typography>
                          {item.result?.full_file_url ? (
                            <Box sx={{ mt: 1 }}>
                              <img src={item.result.full_file_url} alt={item.inspection_item?.label} style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: 4 }} />
                            </Box>
                          ) : (
                            <Typography variant="body2">No photo available</Typography>
                          )}
                        </>
                      )}
                      {type === 'InspectionSignatureItem' && (
                        <>
                          <Typography variant="body2">Signature:</Typography>
                          {item.result?.full_signature_file_url ? (
                            <Box sx={{ mt: 1 }}>
                              <img src={item.result.full_signature_file_url} alt={item.inspection_item?.label} style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: 4 }} />
                            </Box>
                          ) : (
                            <Typography variant="body2">No photo available</Typography>
                          )}
                        </>
                      )}
                      {item.comments?.length > 0 && (
                        <Typography variant="body2" color="text.secondary">Comments: {item.comments.map((c: any) => c.text).join(', ')}</Typography>
                      )}
                      {item.images?.length > 0 && (
                        <Typography variant="body2" color="text.secondary">Images: {item.images.length}</Typography>
                      )}
                    </Box>
                  );
                })}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} variant="outlined" color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100%', py: 3 }}>
      <Container maxWidth="xl">
        <Card>
          <CardHeader
            action={(
              <IconButton onClick={() => window.location.reload()}>
                <RefreshIcon />
              </IconButton>
            )}
            title="Truck Wash"
          />
          <Divider />
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
              >
                <Tabs
                  aria-label="state tabs"
                  value={selectedStateIdx}
                  onChange={handleStateTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  {states.map((state, idx) => (
                    <Tab key={state.id} value={idx} label={state.value} />
                  ))}
                </Tabs>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    mt: { xs: 2, sm: 0 },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Typography variant="body2" sx={{ mr: 1 }}>Showing records from last</Typography>
                  <input
                    type="text"
                    value={days}
                    onChange={handleDaysChange}
                    style={{ width: 60, padding: 6, borderRadius: 4, border: '1px solid #ccc', fontSize: 14 }}
                    placeholder="e.g. 7"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    tabIndex={0}
                  />
                  <Typography variant="body2" sx={{ ml: 1 }}>&nbsp;Day(s)</Typography>
                </Box>
              </Stack>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {/* Filter Chips */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                mb: 2,
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip
                  icon={<ErrorIcon style={{ color: '#d32f2f' }} fontSize="small" />}
                  label={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                        {counts.breached}
                      </Typography>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontWeight: selectedSummary === 'Breached' ? 700 : 400,
                          cursor: 'pointer',
                        }}
                      >
                        Breached ({'>'}14 days)
                      </Typography>
                    </Box>
                  )}
                  clickable
                  onClick={() => setSelectedSummary('Breached')}
                  onDelete={selectedSummary === 'Breached' ? () => setSelectedSummary('') : undefined}
                  deleteIcon={selectedSummary === 'Breached' ? <CancelIcon fontSize="small" /> : undefined}
                  sx={{
                    height: 36,
                    borderRadius: 18,
                    border: 'none',
                    transition: 'all 0.2s ease',
                    fontWeight: selectedSummary === 'Breached' ? 700 : 400,
                  }}
                  variant={selectedSummary === 'Breached' ? 'filled' : 'outlined'}
                />
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip
                  icon={<ReportProblemIcon style={{ color: '#ffa000' }} fontSize="small" />}
                  label={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                        {counts.warning}
                      </Typography>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontWeight: selectedSummary === 'Warning' ? 700 : 400,
                          cursor: 'pointer',
                        }}
                      >
                        Warning ({'>'}11 days)
                      </Typography>
                    </Box>
                  )}
                  clickable
                  onClick={() => setSelectedSummary('Warning')}
                  onDelete={selectedSummary === 'Warning' ? () => setSelectedSummary('') : undefined}
                  deleteIcon={selectedSummary === 'Warning' ? <CancelIcon fontSize="small" /> : undefined}
                  sx={{
                    height: 36,
                    borderRadius: 18,
                    border: 'none',
                    transition: 'all 0.2s ease',
                    fontWeight: selectedSummary === 'Warning' ? 700 : 400,
                  }}
                  variant={selectedSummary === 'Warning' ? 'filled' : 'outlined'}
                />
              </Stack>
            </Box>
            {error && (
              <Typography color="error">{error}</Typography>
            )}
            {/* Desktop Table View */}
            <Hidden mdDown>
              {loading ? (
                <Typography>Loading...</Typography>
              ) : (
                (() => {
                  let filteredSubmissions = submissions;
                  if (selectedSummary === 'Breached') {
                    filteredSubmissions = submissions.filter((s: any) => s.isBreached);
                  } else if (selectedSummary === 'Warning') {
                    filteredSubmissions = submissions.filter((s: any) => s.isWarning);
                  }

                  return (
                    <Table
                      rows={filteredSubmissions}
                      columns={columns}
                      totalRows={filteredSubmissions.length}
                      page={currentPage}
                      onPageChange={({ page }: any) => setCurrentPage(page)}
                      sortModel={sortModel}
                      onSortModelChange={(model: any) => setSortModel(model)}
                      getRowClassName={(params: any) => {
                        if (params.row.isBreached) return 'error';
                        if (params.row.isWarning) return 'warning';
                        return '';
                      }}
                    />
                  );
                })()
              )}
            </Hidden>
            {/* Mobile List View */}
            <Hidden mdUp>
              {loading ? (
                <Typography>Loading...</Typography>
              ) : (
                (() => {
                  let filteredSubmissions = submissions;
                  if (selectedSummary === 'Breached') {
                    filteredSubmissions = submissions.filter((s: any) => s.isBreached);
                  } else if (selectedSummary === 'Warning') {
                    filteredSubmissions = submissions.filter((s: any) => s.isWarning);
                  }

                  return (
                    <MobileDataList
                      rows={filteredSubmissions}
                      renderEachRow={(row: any) => (
                        <Card
                          key={row.id}
                          sx={{
                            marginBottom: '10px',
                            ...(row.isBreached && {
                              borderLeft: '4px solid #d32f2f',
                              backgroundColor: '#ffebee'
                            }),
                            ...(row.isWarning && {
                              borderLeft: '4px solid #ffa000',
                              backgroundColor: '#fff8e1'
                            })
                          }}
                          variant="outlined"
                        >
                          <CardHeader
                            title={row.vehicle?.name || row.license_plate || `Submission #${row.id}`}
                            subheader={(
                              <Box>
                                <Typography variant="body2">
                                  {row.submitted_at ? moment(row.submitted_at).format('LLL') : ''}
                                </Typography>
                                {row.daysSinceSubmission > 11 && (
                                  <Chip
                                    size="small"
                                    label={`${row.daysSinceSubmission} days ago`}
                                    color={row.isBreached ? 'error' : 'warning'}
                                    sx={{ mt: 0.5 }}
                                  />
                                )}
                              </Box>
                            )}
                            action={(
                              <Button variant="outlined" size="small" onClick={() => handleViewSubmission(row)}>
                                View
                              </Button>
                            )}
                          />
                          <Divider />
                          <CardContent>
                            <Typography variant="body2"><b>User:</b> {row.user?.name || row.user}</Typography>
                            <Typography variant="body2"><b>Failed Items:</b> {row.failed_items}</Typography>
                            <Typography variant="body2"><b>License Plate:</b> {row.vehicle?.license_plate || row.license_plate}</Typography>
                            <Typography variant="body2"><b>Date:</b> {row.date}</Typography>
                          </CardContent>
                        </Card>
                      )}
                      totalRows={filteredSubmissions.length}
                      page={currentPage}
                      onPageChange={setCurrentPage}
                    />
                  );
                })()
              )}
            </Hidden>
            {renderSubmissionDialog()}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default VehicleWashSchedule;
